import { useEffect, useState } from 'react'
import DeviceFiltersComponent, { DeviceFilters } from '@/components/DeviceFilters'
import MultiEditToolbar from '@/components/MultiEditToolbar'
import DeviceTable from '@/components/DeviceTable'
import MultiEditModal from '@/components/MultiEditModal'
import SingleEditModal from '@/components/SingleEditModal'
import AuditLogModal from '@/components/AuditLogModal'
import PaginationControls from '@/components/PaginationControls'
import { useDeviceFilters } from '@/hooks/useDeviceFilters'
import { useDeviceSorting } from '@/hooks/useDeviceSorting'
import { useDeviceSelection } from '@/hooks/useDeviceSelection'
import { useColumnResizing } from '@/hooks/useColumnResizing'
import { usePagination } from '@/hooks/usePagination'
import './DevicesPage.css'
import { PAGES } from '@/constants/pages'
import PermissionGuard from '@/components/PermissionGuard'
import { authService, PermissionScope } from '@/services/AuthService'
import { RESOURCES } from '@/constants/resources'
import { PartnerDeviceService } from '@/services/PartnerDeviceService'
import { Device } from '@/models/device'
import { usePartner } from '@/contexts/partnerContext'
import { PartnerCustomFieldService } from '@/services/PartnerCustomFieldService'
import { CustomField } from '@/models/customField'
import { PageLayout } from '@/pages/PageLayout'
import { useLoading } from '@/contexts/LoadingContext'
import toast from '@/utils/toast'

export default function DevicesPage() {
  const { showLoader, hideLoader } = useLoading()
  
  const [devices, setDevices] = useState<Device[]>([])
  const [customFields, setCustomFields] = useState<CustomField[]>([])
  const [showMultiEdit, setShowMultiEdit] = useState(false)
  const [showSingleEdit, setShowSingleEdit] = useState(false)
  const [showAuditLog, setShowAuditLog] = useState(false)
  const [editingDevice, setEditingDevice] = useState<Device | null>(null)
  const [auditDevice, setAuditDevice] = useState<Device | null>(null)

  const { impersonatedPartnerId } = usePartner();

  const canEditDevices = authService.hasPermission(RESOURCES.DEVICES, PermissionScope.UPDATE);

  // Custom hooks for device management
  const { filters, filteredDevices, handleFilterChange, onCustomFieldFilterChange, clearAllFilters } = useDeviceFilters(devices)
  const { sortColumn, sortDirection, sortedDevices, handleSort } = useDeviceSorting(filteredDevices)
  const {
    currentPage,
    pageSize,
    totalPages,
    paginatedItems: paginatedDevices,
    goToPage,
    nextPage,
    previousPage,
    changePageSize,
    totalItems
  } = usePagination(sortedDevices, 100)
  const { selectedDevices, handleSelectDevice, handleSelectAll, clearSelection } = useDeviceSelection(paginatedDevices)
  const { tableRef, columnWidths, handleResizeStart } = useColumnResizing()

  useEffect(() => {
    const loadPageData = async () => {
      if (!shouldGetDevices && !shouldGetCustomFields) return

      showLoader()
      try {
        await Promise.all([
          loadDevices(),
          loadCustomFields()
        ])
      } catch (error) {
        console.error('Error loading page data:', error)
      } finally {
        hideLoader()
      }
    }

    loadPageData()
  }, [impersonatedPartnerId])

  const shouldGetDevices = impersonatedPartnerId !== null || authService.channelPartnerId !== null;
  const shouldGetCustomFields = impersonatedPartnerId !== null || authService.channelPartnerId !== null;

  const loadDevices = async () => {
      try {
        const devices = (await PartnerDeviceService.getAll()).devices
        if (devices == null || devices.length == 0) { 
          setDevices([]);
          return;
        }
        setDevices(devices)
      } catch (error) {
        console.error('Failed to load device:', error)
      }
  }

  const loadCustomFields = async () => {
    try {
      const customFields = (await PartnerCustomFieldService.getAll()).customFields
      if (customFields == null || customFields.length == 0) { 
        setCustomFields([]);
        return;
      }
      setCustomFields(customFields)
    } catch (error) {
      console.error('Failed to load custom fields:', error)
    }
  }

  const onFilterChange = (key: keyof DeviceFilters, value: string | string[]) => {
    handleFilterChange(key, value);
  };

  const handleEdit = (device: Device) => {
    setEditingDevice(device)
    setShowSingleEdit(true)
  }

  const handleLog = (device: Device) => {
    setAuditDevice(device)
    setShowAuditLog(true)
  }

  const handleAuditLogClose = () => {
    setShowAuditLog(false)
    setAuditDevice(null)
  }

  const handleDelete = async (device: Device) => {
    if (!confirm(`Are you sure you want to delete device ${device.serialNumber}?`)) {
      return
    }

    try {
      await PartnerDeviceService.delete(device.id)
      toast.success("Device has successfully been deleted.")
      setDevices(prev => prev.filter(device => device.id !== device.id))
      clearSelection()
    } catch (error) {
      toast.success("Failed to delete device.")
    }
  }

  const handleMultiEdit = () => {
    if (selectedDevices.size === 1) {
      // Single device selected - open single edit modal
      const device = devices.find(d => d.id === Array.from(selectedDevices)[0].id)
      if (device) {
        setEditingDevice(device)
        setShowSingleEdit(true)
      }
    } else {
      // Multiple devices selected - open multi edit modal
      setShowMultiEdit(true)
    }
  }

  const handleMultiEditClose = () => {
    setShowMultiEdit(false)
  }

  const handleSingleEditClose = () => {
    setShowSingleEdit(false)
    setEditingDevice(null)
  }

  const handleMultiEditSave = async (updates: Partial<Device>) => {
    try {
      const deviceUuids = Array.from(selectedDevices).map(device => device.id)
      await PartnerDeviceService.bulkUpdate(deviceUuids, updates.type, updates.simProviderId, updates.subCustomFields?.map(customField => customField.id), updates.firmware, updates.configurationId)
      toast.success("Devices has successfully been updated.")
      // Refresh devices from server to get updated data
      const refreshedDevices = await (await PartnerDeviceService.getAll()).devices
      setDevices(refreshedDevices)

      setShowMultiEdit(false)
      clearSelection()
    } catch (error) {
      toast.error("Failed to update devices.")
    }
  }

  const handleSingleEditSave = async (deviceUuid: string, updates: Partial<Device>) => {
    try {

      await PartnerDeviceService.update(deviceUuid, updates.serialNumber, updates.imei, updates.type, updates.phoneNumber, updates.simSerialNumber, updates.simProviderId, updates.subCustomFields?.map(customField => customField.id), updates.firmware, updates.configurationId)
      toast.success('Device has successfully been updated.')
      // Refresh devices from server to get updated data
      const refreshedDevices = await (await PartnerDeviceService.getAll()).devices
      setDevices(refreshedDevices)

      setShowSingleEdit(false)
      setEditingDevice(null)
    } catch (error) {
      toast.error('Failed to update device.')
    }
  }


    return (
    <PageLayout
      title={PAGES.DEVICES.title}
      heading={PAGES.DEVICES.title}
    >

        <DeviceFiltersComponent
            filters={filters}
            onFilterChange={onFilterChange}
            onCustomFieldFilterChange={onCustomFieldFilterChange}
            onClearAllFilters={clearAllFilters}
            devices={devices}
            customFields={customFields}
            filteredDevices={filteredDevices}
          />

          {filteredDevices.length > 0 && (
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={goToPage}
              onPageSizeChange={changePageSize}
              onNextPage={nextPage}
              onPreviousPage={previousPage}
            />
          )}

          <PermissionGuard resource={RESOURCES.DEVICES} scope={PermissionScope.UPDATE}>
              <MultiEditToolbar
              selectedCount={selectedDevices.size}
              onMultiEdit={handleMultiEdit}
              onClearSelection={clearSelection}
              />
          </PermissionGuard>

          {filteredDevices.length === 0 ? (
            <div className="no-devices">
              {devices.length === 0 ? 'No devices found.' : 'No devices match the current filters.'}
            </div>
          ) : (
            <DeviceTable
              ref={tableRef}
              devices={paginatedDevices}
              customFields={customFields}
              canEditDevices={canEditDevices}
              columnWidths={columnWidths}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              selectedDevices={selectedDevices}
              onSort={handleSort}
              onSelectAll={handleSelectAll}
              onSelectDevice={handleSelectDevice}
              onEdit={handleEdit}
              onLog={handleLog}
              onDelete={handleDelete}
              onResizeStart={handleResizeStart}
            />
          )}

        <PermissionGuard resource={RESOURCES.DEVICES} scope={PermissionScope.UPDATE}>
            {showMultiEdit && (
            <MultiEditModal
                devices={[...selectedDevices]}
                onClose={handleMultiEditClose}
                onSave={handleMultiEditSave}
            />
            )}
        </PermissionGuard>

        <PermissionGuard resource={RESOURCES.DEVICES} scope={PermissionScope.UPDATE}>
            {showSingleEdit && editingDevice && (
            <SingleEditModal
                device={editingDevice}
                devices={devices}
                canEditDeviceIdentity={authService.channelPartnerId === null || authService.channelPartnerId === 0}
                onClose={handleSingleEditClose}
                onSave={handleSingleEditSave}
            />
            )}
        </PermissionGuard>

        {showAuditLog && auditDevice && (
            <AuditLogModal
            entityName="Device"
            entityId={auditDevice.id}
            entityDisplayName={auditDevice.serialNumber}
            device={auditDevice}
            onClose={handleAuditLogClose}
            />
        )}
    </PageLayout>
  )
}